/**
 * /influencers/link — creator-facing influencer link manager.
 *
 * Declares a TanStack Router route and renders the headline + `InfluencerLinkPage`.
 * The route auto-registers against the scaffolded `rootRoute` via a module
 * augmentation in the scaffold's `router.tsx` — the root wire-up cycle picks
 * it up by importing from this module.
 */

import { createRoute, Link } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import { InfluencerLinkPage } from "@/features/influencer-link/InfluencerLinkPage";

// ---------------------------------------------------------------------------
// Route — mounts at `/influencers/link`.
// `validateSearch` reserves room for an `?influencerId=` deep-link so
// operators can open the page from an influencer-detail view with the id
// prefilled, mirroring the pattern used by `campaigns.os-targeting.tsx`.
// ---------------------------------------------------------------------------

interface InfluencerLinkSearch {
	influencerId?: string;
}

export const influencersLinkRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/influencers/link",
	validateSearch: (raw: Record<string, unknown>): InfluencerLinkSearch => {
		const influencerId = raw.influencerId;
		if (typeof influencerId === "string" && influencerId.trim().length > 0) {
			return { influencerId: influencerId.trim() };
		}
		return {};
	},
	component: InfluencersLinkPage,
});

export default influencersLinkRoute;

// ---------------------------------------------------------------------------
// Page — static shell + manager card. The `apiClient` handles the
// `x-tenant-id` header so the feature is tenant-scoped end-to-end.
// ---------------------------------------------------------------------------

export function InfluencersLinkPage() {
	const { influencerId } = influencersLinkRoute.useSearch();

	return (
		<main className="mx-auto flex w-full max-w-4xl flex-col gap-8 px-6 py-10 sm:py-16">
			<Breadcrumb />
			<Heading />
			<InfluencerLinkPage initialInfluencerId={influencerId} />
			<DocHint />
		</main>
	);
}

// ---------------------------------------------------------------------------
// Header fragments — purely presentational. No emojis, no competitor names
// per brand rules. Emerald to sky gradient reserved for the primary heading.
// ---------------------------------------------------------------------------

function Breadcrumb() {
	return (
		<nav aria-label="Breadcrumb" className="text-sm text-zinc-500">
			<ol className="flex flex-wrap items-center gap-1">
				<li>
					<Link
						to="/"
						className="rounded-[6px] px-1 py-0.5 hover:bg-zinc-100 hover:text-zinc-900"
					>
						Dashboard
					</Link>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li>
					<span className="rounded-[6px] px-1 py-0.5">Influencers</span>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li className="text-zinc-900">Links</li>
			</ol>
		</nav>
	);
}

function Heading() {
	return (
		<header className="flex flex-col gap-2">
			<p className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
				Influencer tracking
			</p>
			<h1 className="text-brand-gradient text-3xl font-semibold tracking-tight sm:text-4xl">
				Unique links per creator
			</h1>
			<p className="max-w-prose text-sm text-zinc-600">
				Issue short, tenant-scoped tracking links to each influencer. Revoke
				instantly — revoked slugs return a 410 Gone so downstream creatives
				can tell a dead link from a mistyped one.
			</p>
		</header>
	);
}

function DocHint() {
	return (
		<aside className="rounded-[8px] border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-600">
			<p className="font-medium text-zinc-800">How it works</p>
			<ul className="mt-2 flex list-inside list-disc flex-col gap-1">
				<li>Each link is an 8-character slug on go.trackerity.com.</li>
				<li>
					Revoking greys the row and starts returning 410 Gone on resolution —
					the row stays visible so the operator can audit past activity.
				</li>
				<li>
					Deleting is permanent and removes the row. Prefer revoke for anything
					that was ever shared publicly.
				</li>
			</ul>
		</aside>
	);
}
